import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronLeft, LogOut, PanelLeftClose, Pin, Settings, User, UserPlus } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSidebar } from '@/components/ui/sidebar'
import useLoginStore from '@/store/LoginStore'
import { useSidebarModeStore } from '@/store/SidebarModeStore'
import { performLogout } from '@/lib/auth/performLogout'

type SidebarTopBarProps = {
  user: {
    name: string
    email?: string
    avatar?: string | null
    username?: string
    isLoading?: boolean
  }
}

function getInitials(value?: string) {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  return trimmed.slice(0, 2).toUpperCase()
}

function WorkspaceAvatar({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  if (logoUrl) {
    return (
      <div className="flex aspect-square size-5 items-center justify-center overflow-hidden rounded-md flex-shrink-0">
        <img src={logoUrl} alt={name} className="h-full w-full object-cover" />
      </div>
    )
  }
  return (
    <div className="flex aspect-square size-5 items-center justify-center rounded-md bg-black text-white text-[10px] font-semibold flex-shrink-0">
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

export function SidebarTopBar({ user }: SidebarTopBarProps) {
  const { t } = useTranslation('projects')
  const { t: tNav } = useTranslation('navigation')
  const { isMobile, state, open, toggleSidebar, peek, setOpen } = useSidebar()
  const initials = getInitials(user.username || user.name || user.email)
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const workspaces = useLoginStore((s) => s.workspaces)
  const switchWorkspace = useLoginStore((s) => s.switchWorkspace)
  const navigate = useNavigate()
  const sidebarHidden = useSidebarModeStore((s) => s.hidden)
  const setSidebarHidden = useSidebarModeStore((s) => s.setHidden)

  const [menuOpen, setMenuOpen] = useState(false)

  const isCollapsed = state === 'collapsed'
  const activeWorkspace = workspaces.find((w) => w.workspaceId === workspaceId)
  const hasMultipleWorkspaces = workspaces.length > 1

  const navigateAndClose = useCallback((to: string) => {
    setMenuOpen(false)
    void navigate({ to })
  }, [navigate])

  const handleProfile = () => {
    navigateAndClose(`/${workspaceSlug}/settings`)
  }

  const handleWorkspaceSettings = () => {
    navigateAndClose(`/${workspaceSlug}/settings/workspace`)
  }

  const handleInviteMembers = () => {
    navigateAndClose(`/${workspaceSlug}/settings/members`)
  }

  const handleLogout = async () => {
    setMenuOpen(false)
    // Must await: performLogout clears the IndexedDB auth backup; skipping
    // the await lets the full-page reload race the IDB transaction and
    // restoreFromIndexedDB() re-authenticates on next boot.
    await performLogout()
    window.location.href = '/auth'
  }

  // Hidden mode (design 5c) used to be a second button in the rail footer,
  // next to Dock (MOL-42). Desktop only: a phone's sidebar is already a sheet.
  const handleHideSidebar = () => {
    setMenuOpen(false)
    setSidebarHidden(true)
  }

  const handleSwitchWorkspace = (workspace: (typeof workspaces)[0]) => {
    if (workspace.workspaceId === workspaceId) return
    setMenuOpen(false)
    switchWorkspace(workspace)
    void navigate({ to: `/${workspace.workspaceSlug}` })
  }

  return (
    <div
      className={`flex h-10 shrink-0 items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'}`}
    >
      {/* Workspace trigger */}
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            aria-label={activeWorkspace?.name ?? 'Workspace'}
            className="flex items-center gap-2 h-8 px-1.5 has-[>svg]:px-1.5 hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent"
          >
            <WorkspaceAvatar
              name={activeWorkspace?.name ?? 'W'}
              logoUrl={activeWorkspace?.logoUrl}
            />
            {!isCollapsed && (
              <>
                {user.isLoading ? (
                  <Skeleton className="h-4 w-28" />
                ) : (
                  <span className="text-sm font-medium truncate max-w-[140px]">
                    {activeWorkspace?.name ?? 'Workspace'}
                  </span>
                )}
                <ChevronDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-60 rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          align="start"
          sideOffset={4}
        >
          {/* Profile header */}
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg flex-shrink-0">
                {user.avatar ? (
                  <AvatarImage src={user.avatar} alt={user.name} />
                ) : null}
                <AvatarFallback className="rounded-lg text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                {user.isLoading ? (
                  <>
                    <Skeleton className="h-4 w-24 mb-1" />
                    <Skeleton className="h-3 w-32" />
                  </>
                ) : (
                  <>
                    <span className="truncate font-semibold">{user.name}</span>
                    {user.email && (
                      <span className="truncate text-xs text-muted-foreground">
                        {user.email}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={handleProfile}>
            <User className="mr-2 h-4 w-4" />
            My profile
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={handleWorkspaceSettings}>
            <Settings className="mr-2 h-4 w-4" />
            Workspace settings
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={handleInviteMembers}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite members
          </DropdownMenuItem>

          {!isMobile && !sidebarHidden && (
            <DropdownMenuItem onSelect={handleHideSidebar}>
              <PanelLeftClose className="mr-2 h-4 w-4" />
              {tNav('sidebar.hide')}
            </DropdownMenuItem>
          )}

          {hasMultipleWorkspaces && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <WorkspaceAvatar
                    name={activeWorkspace?.name ?? 'W'}
                    logoUrl={activeWorkspace?.logoUrl}
                  />
                  <span className="ml-2 truncate">{t('sidebar.seeAllWorkspaces')}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-52">
                  {workspaces.map((ws) => (
                    <DropdownMenuItem
                      key={ws.workspaceId}
                      onSelect={() => handleSwitchWorkspace(ws)}
                      className="flex items-center gap-2"
                    >
                      <WorkspaceAvatar name={ws.name} logoUrl={ws.logoUrl} />
                      <span className="flex-1 truncate text-sm">{ws.name}</span>
                      {ws.workspaceId === workspaceId && (
                        <Check className="h-4 w-4 flex-shrink-0" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={handleLogout} className="text-destructive focus:text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Peeking over a rail: Pin docks it for this kind of page (design 5b) */}
      {!open && peek && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          title={tNav('sidebar.pinHint')}
          onClick={() => setOpen(true)}
        >
          <Pin className="h-3 w-3" />
          {tNav('sidebar.pin')}
        </Button>
      )}

      {/* Collapse to rail (only when docked) */}
      {open && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label={tNav('sidebar.collapse')}
          title={tNav('sidebar.collapse')}
          onClick={toggleSidebar}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
